import type { Attempt, Delivery, DeliveryStatus } from '../types';
import { toEpoch } from './format';

export type RollupStatus = DeliveryStatus | 'empty';

export interface DeliveryRollup {
  total: number;
  succeeded: number;
  pending: number;
  deadLettered: number;
  /** Worst status present, so one dead letter is never hidden by nine successes. */
  overall: RollupStatus;
  /** e.g. "2/3 delivered". */
  label: string;
}

export function rollUpDeliveries(deliveries: Delivery[] | undefined | null): DeliveryRollup {
  const list = Array.isArray(deliveries) ? deliveries : [];

  let succeeded = 0;
  let pending = 0;
  let deadLettered = 0;

  for (const delivery of list) {
    if (delivery.status === 'succeeded') succeeded += 1;
    else if (delivery.status === 'dead_lettered') deadLettered += 1;
    else pending += 1;
  }

  const total = list.length;

  let overall: RollupStatus = 'empty';
  if (total > 0) {
    if (deadLettered > 0) overall = 'dead_lettered';
    else if (pending > 0) overall = 'pending';
    else overall = 'succeeded';
  }

  return {
    total,
    succeeded,
    pending,
    deadLettered,
    overall,
    label: total === 0 ? 'No deliveries' : `${succeeded}/${total} delivered`,
  };
}

export function hasPendingDeliveries(deliveries: Delivery[] | undefined | null): boolean {
  return (deliveries ?? []).some((delivery) => delivery.status === 'pending');
}

export interface TimelineNode {
  attempt: Attempt;
  /**
   * Dead time immediately before this attempt started: queue wait for the first
   * attempt, backoff delay for every one after it. Null if timestamps are unusable.
   */
  gapMs: number | null;
  isFirst: boolean;
}

/**
 * Turns a delivery's attempts into timeline nodes carrying the wait that preceded
 * each one. Those waits are what make exponential backoff visible, so they are
 * computed from the attempt clocks rather than assumed from a retry policy.
 */
export function buildTimeline(attempts: Attempt[] | undefined | null, eventCreatedAt: string): TimelineNode[] {
  const sorted = [...(attempts ?? [])].sort((a, b) => a.attemptNumber - b.attemptNumber);
  const eventEpoch = toEpoch(eventCreatedAt);

  return sorted.map((attempt, index) => {
    const startedAt = toEpoch(attempt.startedAt);
    const previous = index === 0 ? eventEpoch : toEpoch(sorted[index - 1].finishedAt);

    const gapMs =
      startedAt !== null && previous !== null ? Math.max(0, startedAt - previous) : null;

    return { attempt, gapMs, isFirst: index === 0 };
  });
}

/**
 * Height in pixels for the connector drawn before each attempt.
 *
 * Scaled linearly against the largest wait in the same delivery so that a
 * doubling backoff reads as a doubling of vertical distance. The exact figure is
 * always printed alongside, so the bar only has to convey the shape.
 */
export function connectorHeight(gapMs: number | null, maxGapMs: number): number {
  const MIN = 20;
  const RANGE = 190;
  if (gapMs === null || maxGapMs <= 0) return MIN;
  return Math.round(MIN + RANGE * Math.min(1, gapMs / maxGapMs));
}

export function maxGap(nodes: TimelineNode[]): number {
  return nodes.reduce((max, node) => (node.gapMs !== null && node.gapMs > max ? node.gapMs : max), 0);
}

/** Total wall-clock time a delivery occupied, successful or not. */
export function totalElapsedMs(delivery: Delivery, attempts: Attempt[]): number | null {
  if (typeof delivery.latencyMs === 'number' && Number.isFinite(delivery.latencyMs)) {
    return delivery.latencyMs;
  }
  const last = attempts[attempts.length - 1];
  if (last && Number.isFinite(last.elapsedSinceEventMs)) return last.elapsedSinceEventMs;
  return null;
}
